import {type DesignerStateType} from '@packages/systems/core/records/DesignerState';
import {Qualified, getPluginName} from '@packages/systems/wfdl-Qualified';
import {createSelector, lruMemoize} from 'reselect';
import {
  LibrariesCapabilitiesMap,
  getLibraryCapabilities,
  LibraryCapabilities,
} from '../utils/getLibraryCapabilities';
import {LibraryCapabilitiesContext, LibraryManifest} from '../types';
import type {DesignerStores} from '@packages/systems/designer/flux';
import {Plugins} from '@packages/systems/wfdl';

const EMPTY_SLUG_MAP: Record<string, string> = {};

const librariesToCapabilities = lruMemoize(
  (
    loadedLibraries: Record<string, LibraryManifest<string>>
  ): LibrariesCapabilitiesMap => {
    if (!loadedLibraries) return {};

    return Object.entries(loadedLibraries).reduce(
      (acc, [pluginName, library]) => {
        acc[pluginName] = getLibraryCapabilities(library);
        return acc;
      },
      {} as Record<string, LibraryCapabilities>
    );
  }
);

/**
 * Selects the context necessary to determine the capabilities of a library.
 * Currently it's just loadedLibraries, but we could eventually supply additional context
 * such as apps, etc.
 */
export const selectLibraryCapabilitiesContext = createSelector(
  (state: {DesignerStore: DesignerStateType}) => state.DesignerStore?.libraries,
  (loadedLibraries): LibraryCapabilitiesContext => ({
    loadedLibraries,
  })
);

/**
 * Returns a map with pluginName: library capabilities
 */
export const selectLibrariesCapabilitiesMap = createSelector(
  selectLibraryCapabilitiesContext,
  ({loadedLibraries}): LibrariesCapabilitiesMap =>
    librariesToCapabilities(loadedLibraries)
);

/**
 * Returns a function that given a library origin, determines its capabilities.
 */
export const selectOriginCapabilities = createSelector(
  selectLibrariesCapabilitiesMap,
  (librariesCapabilities) =>
    (origin: Qualified<string> | null): LibraryCapabilities | null => {
      if (!origin) {
        return null;
      }
      const pluginName = getPluginName(origin);
      return librariesCapabilities[pluginName] || null;
    }
);

/**
 * Returns the current slug map, used to get a unique, human-readable identifier
 * string given a plugin ID.
 */
// Safe since `getPluginsSlugMap` doesn't return a new object:
// eslint-disable-next-line webflow/selector-memoization
export const selectSlugMap = (state: DesignerStores) =>
  state.DesignerStore?.plugins
    ? Plugins.getPluginsSlugMap(state.DesignerStore.plugins)
    : EMPTY_SLUG_MAP;
